import {
  Ban,
  CalendarClock,
  Filter,
  HeartHandshake,
  Magnet,
  Puzzle,
  Receipt,
} from "lucide-react";
import { ACTION_META, HOT_ACTIONS } from "./leadActions";

// Real brand assets live in apps/web/public/assets (served at /assets/*).
const ICON = {
  googleCalendar: "/assets/google_calendar_icon.svg",
  outlook: "/assets/outlook.svg",
  touchworks: "/favicon.svg",
  pipedrive: "/assets/pipedrive.svg",
  trello: "/assets/trello-logo-icon.svg",
  zapsign: "/assets/zapsign_logo_icon.svg",
  asaas: "/assets/asaas-small-logo.svg",
};
import BlocoJJuridico from "./steps/BlocoJJuridico";
import Bloco0Responsavel from "./steps/Bloco0Responsavel";
import Bloco2Triagem from "./steps/Bloco2Triagem";
import BlocoMenuServicos from "./steps/BlocoMenuServicos";
import Bloco7GoLive from "./steps/Bloco7GoLive";
import BriefingReview from "./steps/BriefingReview";
import LeadsDestino from "./steps/LeadsDestino";
import LeadsHorario from "./steps/LeadsHorario";
import TokenStep from "./steps/TokenStep";
import AdvboxConfig from "./steps/AdvboxConfig";
import IdentidadeRegras from "./steps/IdentidadeRegras";

/**
 * Conversational question sequence — one decision per screen, in the same
 * three macro-phases as before (Conexão · Atendimento · IA & Lançamento).
 *
 * Each descriptor: { id, phase, label, title, description?, type, options?,
 * optionsSource?, getValue, buildPatch, skip?, autoAdvance?, Component? }.
 * Generic types (single/multi/text) render through QuestionScreen; custom
 * screens keep their own component and receive { clientId, briefing, autosave,
 * completeError, onJumpToStep } like before. Patch shapes are unchanged — the
 * backend whitelist and the flow generator consume the exact same fields.
 */

export const GOAL_OPTIONS = [
  {
    id: "captacao",
    label: "Captar mais clientes",
    desc: "Transformar contatos do WhatsApp em leads qualificados.",
    icon: Magnet,
  },
  {
    id: "triagem",
    label: "Triar e qualificar quem chega",
    desc: "Filtrar os casos e priorizar o que importa.",
    icon: Filter,
  },
  {
    id: "agendamento",
    label: "Agendar atendimentos",
    desc: "Marcar consultas e reuniões sem esforço manual.",
    icon: CalendarClock,
  },
  {
    id: "cobranca",
    label: "Cobrança e financeiro",
    desc: "Avisos de pagamento e financeiro no automático.",
    icon: Receipt,
  },
  {
    id: "pos_venda",
    label: "Pós-venda e acompanhamento",
    desc: "Manter clientes informados sobre seus processos.",
    icon: HeartHandshake,
  },
];

export const GOAL_LABELS = Object.fromEntries(GOAL_OPTIONS.map((g) => [g.id, g.label]));

export const TOOL_OPTIONS = {
  crm: [
    { id: "touchworks", label: "CRM da TouchWorks", desc: "O funil de vendas daqui da plataforma.", iconSrc: ICON.touchworks },
    { id: "pipedrive", label: "Pipedrive", iconSrc: ICON.pipedrive },
    { id: "trello", label: "Trello", iconSrc: ICON.trello },
    { id: "outro", label: "Outro CRM", icon: Puzzle },
    { id: "nenhum", label: "Não usaremos CRM", icon: Ban },
  ],
  agenda: [
    { id: "google", label: "Google Agenda", iconSrc: ICON.googleCalendar },
    { id: "outlook", label: "Outlook / Teams", iconSrc: ICON.outlook },
    { id: "outra", label: "Outra", icon: Puzzle },
    { id: "nenhuma", label: "Não usamos agenda digital", icon: Ban },
  ],
  signature: [
    { id: "zapsign", label: "ZapSign", iconSrc: ICON.zapsign },
    { id: "outra", label: "Outra ferramenta digital", desc: "DocuSign, Clicksign, D4Sign…", icon: Puzzle },
    { id: "nenhuma", label: "No papel / não usamos", icon: Ban },
  ],
  billing: [
    { id: "asaas", label: "Asaas", iconSrc: ICON.asaas },
    { id: "outro", label: "Outro sistema", icon: Puzzle },
    { id: "nenhum", label: "Não usamos", icon: Ban },
  ],
};

export const TOOL_LABELS = Object.fromEntries(
  Object.entries(TOOL_OPTIONS).map(([k, opts]) => [
    k,
    Object.fromEntries(opts.map((o) => [o.id, o.label])),
  ]),
);

export const PROCESS_OPTIONS = {
  qualifier: [
    { id: "advogado", label: "Advogado(a) responsável" },
    { id: "secretaria", label: "Secretaria / recepção" },
    { id: "socio", label: "Sócio(a) / gestor" },
    { id: "ninguem", label: "Quem atende resolve" },
  ],
  contractApprover: [
    { id: "advogado_responsavel", label: "O(a) advogado(a) do caso" },
    { id: "socio", label: "Sócio(a) / gestor" },
    { id: "administrativo", label: "Financeiro / administrativo" },
    { id: "outro", label: "Outro" },
  ],
  followUp: [
    { id: "crm", label: "Pelo CRM" },
    { id: "planilha", label: "Planilha ou agenda" },
    { id: "manual", label: "Manual, sem rotina fixa" },
    { id: "nenhum", label: "Não fazemos follow-up" },
  ],
};

export const PROCESS_LABELS = Object.fromEntries(
  Object.entries(PROCESS_OPTIONS).map(([k, opts]) => [
    k,
    Object.fromEntries(opts.map((o) => [o.id, o.label])),
  ]),
);

const TONES = [
  { id: "formal", label: "Formal e sóbrio" },
  { id: "acolhedor", label: "Acolhedor" },
  { id: "direto", label: "Direto e objetivo" },
];

export const TONE_LABELS = Object.fromEntries(TONES.map((t) => [t.id, t.label]));

// Old 10-step wizard ids → first question of the equivalent stretch, so
// briefings started before the conversational wizard resume in place.
export const LEGACY_STEP_MAP = {
  juridico: "juridico",
  responsavel: "responsavel",
  areas: "areas",
  triagem: "triagem",
  leads: "leads_acao",
  menu: "menu",
  integracoes: "ferramenta_crm",
  identidade: "identidade_nome",
  golive: "kickoff",
  revisao: "revisao",
  review: "revisao",
};

const selectedAreas = (b) => b.areas?.selected || [];

const TokenZapsign = (props) => <TokenStep {...props} provider="zapsign" />;
const TokenAsaas = (props) => <TokenStep {...props} provider="asaas" />;

export const QUESTIONS = [
  // ── Fase 1 · Conexão ────────────────────────────────────────────────
  {
    id: "juridico",
    phase: "Conexão",
    label: "Sistema jurídico",
    Component: BlocoJJuridico,
  },
  {
    id: "responsavel",
    phase: "Conexão",
    label: "Responsável",
    Component: Bloco0Responsavel,
  },

  // ── Fase 2 · Atendimento ────────────────────────────────────────────
  {
    id: "objetivo",
    phase: "Atendimento",
    label: "Objetivo",
    title: "Qual é a principal dor que a automação deve resolver?",
    description: "Escolha UMA. É ela que orienta como montamos seu fluxo.",
    type: "single",
    options: GOAL_OPTIONS,
    autoAdvance: true,
    getValue: (b) => b.automationGoal?.primary || null,
    buildPatch: (v, b) => ({
      automationGoal: {
        primary: v,
        secondary: (b.automationGoal?.secondary || []).filter((s) => s !== v),
      },
    }),
  },
  {
    id: "objetivo_secundario",
    phase: "Atendimento",
    label: "Objetivos extras",
    title: "Mais alguma frente que também importa?",
    description: "Opcional — marque quantas quiser e avance.",
    type: "multi",
    options: (b) => GOAL_OPTIONS.filter((g) => g.id !== b.automationGoal?.primary),
    getValue: (b) => b.automationGoal?.secondary || [],
    buildPatch: (v, b) => ({
      automationGoal: { primary: b.automationGoal?.primary || null, secondary: v },
    }),
  },
  {
    id: "areas",
    phase: "Atendimento",
    label: "Áreas",
    title: "Quais áreas o escritório atende?",
    description: "Marque tudo que vocês atendem hoje.",
    type: "multi",
    optionsSource: "areas",
    getValue: (b) => selectedAreas(b),
    buildPatch: (v, b) => ({
      areas: {
        selected: v,
        declined: (b.areas?.declined || []).filter((a) => !v.includes(a)),
      },
      primaryArea: b.primaryArea && v.includes(b.primaryArea) ? b.primaryArea : null,
    }),
  },
  {
    id: "area_principal",
    phase: "Atendimento",
    label: "Área principal",
    title: "E qual é a área principal?",
    description: "A IA prioriza esses casos.",
    type: "single",
    optionsSource: "areasSelected",
    autoAdvance: true,
    skip: (b) => selectedAreas(b).length === 0,
    getValue: (b) => b.primaryArea || null,
    buildPatch: (v) => ({ primaryArea: v }),
  },
  {
    id: "areas_recusadas",
    phase: "Atendimento",
    label: "Áreas recusadas",
    title: "Alguma área que vocês fazem questão de recusar?",
    description: "A IA recusa educadamente e encerra a conversa.",
    type: "multi",
    tone: "destructive",
    optionsSource: "areasDeclinable",
    emptyMessage: "Todas as áreas do catálogo já estão selecionadas.",
    skip: (b) => selectedAreas(b).length === 0,
    getValue: (b) => b.areas?.declined || [],
    buildPatch: (v, b) => ({ areas: { selected: selectedAreas(b), declined: v } }),
  },
  {
    id: "triagem",
    phase: "Atendimento",
    label: "Triagem",
    skip: (b) => selectedAreas(b).length === 0,
    Component: Bloco2Triagem,
  },
  {
    id: "leads_acao",
    phase: "Atendimento",
    label: "Lead quente",
    title: "O que fazer quando a IA qualifica um lead?",
    description: "Só perguntamos o que fizer sentido para a opção escolhida.",
    type: "single",
    options: HOT_ACTIONS,
    autoAdvance: true,
    getValue: (b) => b.leadHandling?.hotAction || null,
    buildPatch: (v, b) => {
      const lh = b.leadHandling || {};
      return {
        leadHandling: {
          ...lh,
          hotAction: v,
          recipient: ACTION_META[v]?.recipient === "none" ? null : lh.recipient || null,
        },
      };
    },
  },
  {
    id: "leads_destino",
    phase: "Atendimento",
    label: "Destino do lead",
    skip: (b) => {
      const meta = ACTION_META[b.leadHandling?.hotAction];
      return !meta || meta.recipient === "none";
    },
    Component: LeadsDestino,
  },
  {
    id: "leads_horario",
    phase: "Atendimento",
    label: "Horário",
    skip: (b) => !ACTION_META[b.leadHandling?.hotAction]?.hours,
    Component: LeadsHorario,
  },
  {
    id: "menu",
    phase: "Atendimento",
    label: "Menu de serviços",
    skip: (b) => !b.juridicalProvider || b.juridicalProvider === "none",
    Component: BlocoMenuServicos,
  },
  {
    id: "ferramenta_crm",
    phase: "Atendimento",
    label: "CRM",
    title: "Qual CRM você vai usar?",
    description: "Ajuda a decidir para onde os leads vão.",
    type: "single",
    options: TOOL_OPTIONS.crm,
    autoAdvance: true,
    getValue: (b) => b.toolsInUse?.crm || null,
    buildPatch: (v, b) => ({ toolsInUse: { ...(b.toolsInUse || {}), crm: v } }),
  },
  {
    id: "ferramenta_agenda",
    phase: "Atendimento",
    label: "Agenda",
    title: "Qual agenda o escritório usa?",
    description: "Para agendamentos automáticos com link de reunião.",
    type: "single",
    options: TOOL_OPTIONS.agenda,
    autoAdvance: true,
    getValue: (b) => b.toolsInUse?.agenda || null,
    buildPatch: (v, b) => ({ toolsInUse: { ...(b.toolsInUse || {}), agenda: v } }),
  },
  {
    id: "ferramenta_assinatura",
    phase: "Atendimento",
    label: "Assinatura digital",
    title: "Como vocês assinam contratos?",
    description: "Com a ZapSign conectada, a IA envia contratos direto no WhatsApp.",
    type: "single",
    options: TOOL_OPTIONS.signature,
    autoAdvance: true,
    getValue: (b) => b.toolsInUse?.signature || null,
    buildPatch: (v, b) => ({ toolsInUse: { ...(b.toolsInUse || {}), signature: v } }),
  },
  {
    id: "token_zapsign",
    phase: "Atendimento",
    label: "Token ZapSign",
    skip: (b) => b.toolsInUse?.signature !== "zapsign",
    Component: TokenZapsign,
  },
  {
    id: "ferramenta_financeiro",
    phase: "Atendimento",
    label: "Financeiro",
    title: "E as cobranças, saem por onde?",
    description: "Com o Asaas conectado, avisos de cobrança saem no automático.",
    type: "single",
    options: TOOL_OPTIONS.billing,
    autoAdvance: true,
    getValue: (b) => b.toolsInUse?.billing || null,
    buildPatch: (v, b) => ({ toolsInUse: { ...(b.toolsInUse || {}), billing: v } }),
  },
  {
    id: "token_asaas",
    phase: "Atendimento",
    label: "Token Asaas",
    skip: (b) => b.toolsInUse?.billing !== "asaas",
    Component: TokenAsaas,
  },
  {
    id: "advbox_config",
    phase: "Atendimento",
    label: "AdvBox",
    skip: (b) => b.juridicalProvider !== "advbox",
    Component: AdvboxConfig,
  },
  {
    id: "processo_qualificacao",
    phase: "Atendimento",
    label: "Qualificação",
    title: "Quem qualifica os leads hoje?",
    description: "Quem conversa primeiro e decide se o caso segue.",
    type: "single",
    options: PROCESS_OPTIONS.qualifier,
    autoAdvance: true,
    getValue: (b) => b.salesProcess?.qualifier || null,
    buildPatch: (v, b) => ({ salesProcess: { ...(b.salesProcess || {}), qualifier: v } }),
  },
  {
    id: "processo_contrato",
    phase: "Atendimento",
    label: "Contratos",
    title: "Quem aprova e gera o contrato?",
    type: "single",
    options: PROCESS_OPTIONS.contractApprover,
    autoAdvance: true,
    getValue: (b) => b.salesProcess?.contractApprover || null,
    buildPatch: (v, b) => ({
      salesProcess: { ...(b.salesProcess || {}), contractApprover: v },
    }),
  },
  {
    id: "processo_followup",
    phase: "Atendimento",
    label: "Follow-up",
    title: "Como é feito o follow-up de propostas?",
    type: "single",
    options: PROCESS_OPTIONS.followUp,
    getValue: (b) => b.salesProcess?.followUp || null,
    buildPatch: (v, b) => ({ salesProcess: { ...(b.salesProcess || {}), followUp: v } }),
    notes: {
      label: "Algo mais sobre o processo comercial? (opcional)",
      placeholder: "ex.: quem faz o primeiro contato, prazos de resposta, metas…",
      getValue: (b) => b.salesProcess?.notes || "",
      buildPatch: (v, b) => ({ salesProcess: { ...(b.salesProcess || {}), notes: v } }),
    },
  },

  // ── Fase 3 · IA & Lançamento ────────────────────────────────────────
  {
    id: "identidade_nome",
    phase: "IA & Lançamento",
    label: "Nome da IA",
    title: "Como o assistente vai se chamar?",
    description: "É assim que ele se apresenta no WhatsApp.",
    type: "text",
    inputLabel: "Nome do assistente",
    placeholder: 'ex.: "Sofia", "Assistente do Dr. Silva"',
    getValue: (b) => b.assistantName || "",
    buildPatch: (v) => ({ assistantName: v }),
  },
  {
    id: "identidade_tom",
    phase: "IA & Lançamento",
    label: "Tom",
    title: "Qual tom combina com o escritório?",
    type: "single",
    options: TONES,
    autoAdvance: true,
    getValue: (b) => b.assistantTone || null,
    buildPatch: (v) => ({ assistantTone: v }),
  },
  {
    id: "identidade_regras",
    phase: "IA & Lançamento",
    label: "Regras",
    Component: IdentidadeRegras,
  },
  {
    id: "kickoff",
    phase: "IA & Lançamento",
    label: "Kickoff e Treinamento",
    Component: Bloco7GoLive,
  },
  {
    id: "revisao",
    phase: "IA & Lançamento",
    label: "Revisão",
    Component: BriefingReview,
  },
];
